import { type ExpandableCatalog, CATALOG_IDS } from '../models/catalog';
import { normalizeFieldText } from '../fieldPatterns';
import { catalogRepo } from '../repos/catalogRepo';

/**
 * Check if version1 is newer than version2
 */
function isVersionNewer(version1: string, version2: string): boolean {
  const normalizeVersion = (v: string): string => {
    // Remove 'v' prefix and ensure semantic version format
    return v.replace(/^v/i, '').replace(/[^\d.]/g, '');
  };

  const v1 = normalizeVersion(version1).split('.').map(Number);
  const v2 = normalizeVersion(version2).split('.').map(Number);

  // Compare major.minor.patch
  for (let i = 0; i < Math.max(v1.length, v2.length); i++) {
    const part1 = v1[i] || 0;
    const part2 = v2[i] || 0;

    if (part1 > part2) return true;
    if (part1 < part2) return false;
  }

  return false; // Versions are equal
}

/**
 * Update only catalogs that haven't been modified by the user
 */
async function updateNonModifiedCatalogs(
  defaultCatalogs: Record<string, ExpandableCatalog>
): Promise<void> {
  const catalogIds = Object.keys(defaultCatalogs);
  const updatePromises: Promise<void>[] = [];

  for (const catalogId of catalogIds) {
    const isUserModified = await catalogRepo.isUserModified(catalogId);

    if (!isUserModified) {
      console.log(`[Catalog] Updating non-modified catalog: ${catalogId}`);
      updatePromises.push(catalogRepo.saveCatalog(defaultCatalogs[catalogId]));
    } else {
      console.log(`[Catalog] Skipping user-modified catalog: ${catalogId}`);
    }
  }

  if (updatePromises.length > 0) {
    await Promise.all(updatePromises);
  }
}

/**
 * Embedded fallback defaults for development
 */
function getEmbeddedDefaults(): {
  version: string;
  lastUpdated: string;
  catalogs: Record<string, ExpandableCatalog>;
} {
  const now = new Date().toISOString();

  return {
    version: '1.3.2',
    lastUpdated: now,
    catalogs: {
      [CATALOG_IDS.TOXIC_GASES]: {
        id: CATALOG_IDS.TOXIC_GASES,
        label: 'Gas Tossici',
        description: 'Elenco dei gas tossici utilizzati negli impianti di refrigerazione',
        behavior: 'strict',
        isUserEditable: true,
        version: '1.0.0',
        isUserModified: false,
        createdAt: now,
        updatedAt: now,
        options: [
          {
            id: 'ammoniaca-anidra',
            label: 'Ammoniaca anidra',
            description: 'Ammoniaca senza acqua (NH3 anidra)',
            isUserDefined: false,
            isActive: true,
            createdAt: now,
          },
          {
            id: 'anidride-solforosa',
            label: 'Anidride solforosa',
            description: 'Gas tossico utilizzato in alcune applicazioni',
            isUserDefined: false,
            isActive: true,
            createdAt: now,
          },
          {
            id: 'cianuri',
            label: 'Cianuri',
            description: 'Composti altamente tossici (uso industriale)',
            isUserDefined: false,
            isActive: true,
            createdAt: now,
          },
        ],
      },
      [CATALOG_IDS.PLANT_TYPES]: {
        id: CATALOG_IDS.PLANT_TYPES,
        label: 'Tipi Impianto',
        description: 'Classificazione dei tipi di impianto di refrigerazione',
        behavior: 'suggestions',
        isUserEditable: true,
        version: '1.0.0',
        isUserModified: false,
        createdAt: now,
        updatedAt: now,
        options: [
          {
            id: 'refrigerazione-industriale',
            label: 'Refrigerazione industriale',
            description: 'Impianti di refrigerazione per uso industriale',
            isUserDefined: false,
            isActive: true,
            createdAt: now,
          },
          {
            id: 'logistica-del-freddo',
            label: 'Logistica del freddo',
            description: 'Impianti per catena del freddo e stoccaggio',
            isUserDefined: false,
            isActive: true,
            createdAt: now,
          },
        ],
      },
      [CATALOG_IDS.PLANT_LOCATIONS]: {
        id: CATALOG_IDS.PLANT_LOCATIONS,
        label: 'Localizzazioni Impianto',
        description: "Posizioni tipiche dell'impianto nello stabilimento",
        behavior: 'suggestions',
        isUserEditable: true,
        version: '1.0.0',
        isUserModified: false,
        createdAt: now,
        updatedAt: now,
        options: [
          {
            id: 'sala-macchine-principale',
            label: 'Sala macchine principale',
            description: 'Area principale con equipaggiamento critico',
            isUserDefined: false,
            isActive: true,
            createdAt: now,
          },
          {
            id: 'area-picking',
            label: 'Area picking',
            description: 'Zona prelievo merci refrigerate',
            isUserDefined: false,
            isActive: true,
            createdAt: now,
          },
          {
            id: 'magazzino-bassa-temperatura',
            label: 'Magazzino bassa temperatura',
            description: 'Area stoccaggio a bassa temperatura',
            isUserDefined: false,
            isActive: true,
            createdAt: now,
          },
          {
            id: 'reparto-confezionamento',
            label: 'Reparto confezionamento',
            description: 'Area confezionamento prodotti',
            isUserDefined: false,
            isActive: true,
            createdAt: now,
          },
          {
            id: 'tetto-tecnico',
            label: 'Tetto tecnico',
            description: 'Area superiore con impianti tecnici',
            isUserDefined: false,
            isActive: true,
            createdAt: now,
          },
        ],
      },
      [CATALOG_IDS.COMPONENT_CATEGORIES]: {
        id: CATALOG_IDS.COMPONENT_CATEGORIES,
        label: 'Categorie Componenti',
        description: 'Tipologie di componenti per impianti di refrigerazione',
        behavior: 'suggestions',
        isUserEditable: true,
        version: '1.0.0',
        isUserModified: false,
        createdAt: now,
        updatedAt: now,
        options: [
          {
            id: 'compressore',
            label: 'Compressore',
            description: 'Gruppo compressione gas refrigerante',
            isUserDefined: false,
            isActive: true,
            createdAt: now,
          },
          {
            id: 'separatore-olio-nh3',
            label: 'Separatore olio/NH3',
            description: 'Separatore olio e ammoniaca',
            isUserDefined: false,
            isActive: true,
            createdAt: now,
          },
          {
            id: 'separatore-liquido-gas',
            label: 'Separatore liquido/gas',
            description: 'Separatore fase liquida e gassosa',
            isUserDefined: false,
            isActive: true,
            createdAt: now,
          },
          {
            id: 'ricevitore',
            label: 'Ricevitore',
            description: 'Serbatoio accumulo liquido refrigerante',
            isUserDefined: false,
            isActive: true,
            createdAt: now,
          },
          {
            id: 'condensatore',
            label: 'Condensatore',
            description: 'Scambiatore di calore condensazione',
            isUserDefined: false,
            isActive: true,
            createdAt: now,
          },
          {
            id: 'scambiatore',
            label: 'Scambiatore',
            description: 'Scambiatore di calore generico',
            isUserDefined: false,
            isActive: true,
            createdAt: now,
          },
          {
            id: 'pompa-nh3',
            label: 'Pompa NH3',
            description: 'Pompa circolazione ammoniaca',
            isUserDefined: false,
            isActive: true,
            createdAt: now,
          },
          {
            id: 'evaporatore',
            label: 'Evaporatore',
            description: 'Scambiatore lato evaporazione',
            isUserDefined: false,
            isActive: true,
            createdAt: now,
          },
          {
            id: 'bombole',
            label: 'Bombole',
            description: 'Bombole gas refrigerante',
            isUserDefined: false,
            isActive: true,
            createdAt: now,
          },
        ],
      },
      [CATALOG_IDS.THRESHOLD_TYPES]: {
        id: CATALOG_IDS.THRESHOLD_TYPES,
        label: 'Tipi soglia allarme',
        description: 'Tipologie di soglia per rilevazione gas',
        behavior: 'strict',
        isUserEditable: false,
        version: '1.0.0',
        isUserModified: false,
        createdAt: now,
        updatedAt: now,
        options: [
          {
            id: 'tossicita',
            label: 'Tossicità',
            description: "Soglie legate alla tossicità dell'ammoniaca",
            isUserDefined: false,
            isActive: true,
            createdAt: now,
          },
          {
            id: 'esplosivita',
            label: 'Esplosività',
            description: 'Soglie legate al limite di esplosività (LEL)',
            isUserDefined: false,
            isActive: true,
            createdAt: now,
          },
        ],
      },
      [CATALOG_IDS.THRESHOLD_UNITS]: {
        id: CATALOG_IDS.THRESHOLD_UNITS,
        label: 'Unità soglia allarme',
        description: 'Unità di misura per soglie di intervento',
        behavior: 'strict',
        isUserEditable: false,
        version: '1.0.0',
        isUserModified: false,
        createdAt: now,
        updatedAt: now,
        options: [
          {
            id: 'ppm',
            label: 'ppm',
            description: 'Parti per milione',
            isUserDefined: false,
            isActive: true,
            createdAt: now,
          },
          {
            id: 'percent_lel',
            label: '% LIE',
            description: 'Percentuale del limite inferiore di esplosività',
            isUserDefined: false,
            isActive: true,
            createdAt: now,
          },
        ],
      },
      [CATALOG_IDS.THRESHOLD_ACTIONS]: {
        id: CATALOG_IDS.THRESHOLD_ACTIONS,
        label: 'Azioni soglia allarme',
        description: 'Azioni attivate al superamento delle soglie',
        behavior: 'suggestions',
        isUserEditable: true,
        version: '1.1.0',
        isUserModified: false,
        createdAt: now,
        updatedAt: now,
        options: [
          {
            id: 'dialer',
            label: 'Combinatore telefonico',
            description: 'Chiamata automatica ai referenti',
            isUserDefined: false,
            isActive: true,
            createdAt: now,
          },
          {
            id: 'siren',
            label: 'Allarme acustico',
            description: 'Sirena o segnale sonoro',
            isUserDefined: false,
            isActive: true,
            createdAt: now,
          },
          {
            id: 'signal',
            label: 'Segnalazione luminosa',
            description: 'Lampeggianti o indicatori visivi',
            isUserDefined: false,
            isActive: true,
            createdAt: now,
          },
          {
            id: 'fans',
            label: 'Estrattori/Ventilatori',
            description: 'Avvio sistemi di ventilazione',
            isUserDefined: false,
            isActive: true,
            createdAt: now,
          },
          {
            id: 'power_cutoff',
            label: 'Sgancio elettrico',
            description: 'Interruzione alimentazione',
            isUserDefined: false,
            isActive: true,
            createdAt: now,
          },
          {
            id: 'scrubber',
            label: 'Scrubber',
            description: 'Attivazione scrubber',
            isUserDefined: false,
            isActive: true,
            createdAt: now,
          },
          {
            id: 'shutdown',
            label: 'Arresto impianto',
            description: "Fermata completa dell'impianto",
            isUserDefined: false,
            isActive: true,
            createdAt: now,
          },
          {
            id: 'alarm_only',
            label: 'Solo allarme',
            description: 'Solo notifica senza interventi automatici',
            isUserDefined: false,
            isActive: true,
            createdAt: now,
          },
        ],
      },
    },
  };
}

/**
 * Service for managing catalog initialization and default loading
 * Ensures user modifications are never overwritten
 */
export const catalogService = {
  /**
   * Initialize catalogs with defaults if needed
   * This loads default catalogs only when:
   * 1. Database is empty (first run)
   * 2. Default catalog version is newer than current version
   * 3. User has not modified the catalog
   */
  async initializeIfEmpty(): Promise<void> {
    try {
      const currentVersion = await catalogRepo.getCatalogVersion();
      const defaultCatalogs = await this.loadDefaultCatalogs();

      // If no version exists, this is first run - load all defaults
      if (!currentVersion) {
        console.log('[Catalog] First run - loading default catalogs');
        await this.loadAllDefaults(defaultCatalogs);
        await catalogRepo.setCatalogVersion(defaultCatalogs.version);
        return;
      }

      // If default version is newer, update only non-modified catalogs
      if (isVersionNewer(defaultCatalogs.version, currentVersion)) {
        console.log(`[Catalog] Updating from v${currentVersion} to v${defaultCatalogs.version}`);
        await updateNonModifiedCatalogs(defaultCatalogs.catalogs);
        await catalogRepo.setCatalogVersion(defaultCatalogs.version);
        return;
      }

      console.log(`[Catalog] Catalogs up to date (v${currentVersion})`);
    } catch (error) {
      console.error('[Catalog] Failed to initialize catalogs:', error);
      throw error;
    }
  },

  /**
   * Load default catalogs from external JSON file
   */
  async loadDefaultCatalogs(): Promise<{
    version: string;
    lastUpdated: string;
    catalogs: Record<string, ExpandableCatalog>;
  }> {
    try {
      // In production, this will be served from the public directory
      const response = await fetch('/catalogs/default-catalogs.json');
      if (!response.ok) {
        throw new Error(`Failed to load default catalogs: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('[Catalog] Failed to load default catalogs file:', error);

      // Fallback to embedded defaults for development
      return getEmbeddedDefaults();
    }
  },

  /**
   * Load all default catalogs into storage
   */
  async loadAllDefaults(defaultData: {
    version: string;
    catalogs: Record<string, ExpandableCatalog>;
  }): Promise<void> {
    const catalogPromises = Object.values(defaultData.catalogs).map((catalog) =>
      catalogRepo.saveCatalog(catalog)
    );

    await Promise.all(catalogPromises);
  },

  /**
   * Get catalog options as a simple array for form usage
   */
  async getCatalogOptions(
    catalogId: string,
    options?: { includeInactive?: boolean }
  ): Promise<
    Array<{
      id: string;
      label: string;
      description?: string;
      isActive?: boolean;
    }>
  > {
    const catalog = await catalogRepo.getCatalog(catalogId);

    if (!catalog) {
      throw new Error(`Catalog "${catalogId}" not found. Ensure catalogs are initialized.`);
    }

    const includeInactive = options?.includeInactive ?? false;
    return catalog.options
      .filter((option) => includeInactive || option.isActive !== false)
      .map((option) => ({
        id: option.id,
        label: option.label,
        description: option.description,
        isActive: option.isActive,
      }));
  },

  /**
   * Get specific catalog option by ID
   */
  async getCatalogOption(
    catalogId: string,
    optionId: string
  ): Promise<{
    id: string;
    label: string;
    description?: string;
    isActive?: boolean;
  } | null> {
    const options = await this.getCatalogOptions(catalogId, {
      includeInactive: true,
    });
    return options.find((option) => option.id === optionId) || null;
  },

  async ensureCatalogOption(
    catalogId: string,
    input: { label: string; description?: string; optionId?: string }
  ): Promise<{
    id: string;
    label: string;
    description?: string;
    isActive?: boolean;
  }> {
    const catalog = await catalogRepo.getCatalog(catalogId);
    if (!catalog) {
      throw new Error(`Catalog "${catalogId}" not found`);
    }
    if (!catalog.isUserEditable) {
      throw new Error(`Catalog "${catalogId}" non modificabile`);
    }

    const normalizedLabel = normalizeFieldText(input.label);
    const existing = catalog.options.find(
      (option) => option.label.toLowerCase() === normalizedLabel.toLowerCase()
    );
    if (existing) {
      return {
        id: existing.id,
        label: existing.label,
        description: existing.description,
        isActive: existing.isActive,
      };
    }

    const created = await catalogRepo.addCatalogOption(catalogId, {
      id: input.optionId,
      label: normalizedLabel,
      description: input.description,
    });

    return {
      id: created.id,
      label: created.label,
      description: created.description,
      isActive: created.isActive,
    };
  },
};
